import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/theme-xcode'
import SydButton from '../../../../../../commonDesign/Button'
import { computeLineDiff } from './diffUtils'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--border-radius-lg)',
    width: '92vw',
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--color-neutral-900)'
  },
  description: {
    fontSize: '13px',
    color: 'var(--color-neutral-500)',
    marginLeft: '12px',
    fontWeight: 400
  },
  diffContainer: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  diffPane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:first-child': {
      borderRight: '1px solid var(--color-neutral-200)'
    }
  },
  paneHeader: {
    padding: '6px 16px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  paneHeaderOld: {
    backgroundColor: 'var(--color-danger-100)',
    color: 'var(--color-danger-700)'
  },
  paneHeaderNew: {
    backgroundColor: 'var(--color-success-100)',
    color: 'var(--color-success-700)'
  },
  editorWrapper: {
    flex: 1,
    overflow: 'hidden',
    '& .ace_editor': {
      fontFamily: 'var(--font-mono, monospace) !important'
    },
    '& .diff-line-removed': {
      position: 'absolute',
      backgroundColor: 'rgba(239, 68, 68, 0.18) !important',
      borderLeft: '3px solid var(--color-danger-400)'
    },
    '& .diff-line-added': {
      position: 'absolute',
      backgroundColor: 'rgba(34, 197, 94, 0.18) !important',
      borderLeft: '3px solid var(--color-success-500)'
    },
    '& .diff-gutter-removed': {
      backgroundColor: 'rgba(239, 68, 68, 0.25) !important',
      color: 'var(--color-danger-600) !important'
    },
    '& .diff-gutter-added': {
      backgroundColor: 'rgba(34, 197, 94, 0.25) !important',
      color: 'var(--color-success-700) !important'
    }
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderTop: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  stats: {
    fontSize: '12px',
    color: 'var(--color-neutral-500)',
    display: 'flex',
    gap: '12px'
  },
  statAdded: {
    color: 'var(--color-success-600)',
    fontWeight: 600
  },
  statRemoved: {
    color: 'var(--color-danger-600)',
    fontWeight: 600
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    padding: '4px',
    '&:hover': {
      color: 'var(--color-neutral-600)'
    }
  },
  footerActions: {
    display: 'flex',
    gap: '8px'
  }
}))

function applyMarkers (editor, rows, lineClass, gutterClass) {
  if (!editor) return
  const session = editor.getSession()

  session.clearBreakpoints()
  for (const row of rows) {
    session.addGutterDecoration(row, gutterClass)
  }

  const Range = window.ace?.require('ace/range')?.Range
  if (Range) {
    for (const row of rows) {
      session.addMarker(
        new Range(row, 0, row, Infinity),
        lineClass,
        'fullLine',
        false
      )
    }
  }
}

function DiffEditor ({ source, markerRows, lineClass, gutterClass, editorRef, onScroll }) {
  const classes = useStyles()

  const handleLoad = useCallback((editor) => {
    editorRef.current = editor
    applyMarkers(editor, markerRows, lineClass, gutterClass)

    editor.getSession().on('changeScrollTop', (scrollTop) => {
      if (onScroll) onScroll(scrollTop)
    })
    editor.getSession().on('changeScrollLeft', (scrollLeft) => {
      if (onScroll) onScroll(null, scrollLeft)
    })
  }, [markerRows, lineClass, gutterClass, editorRef, onScroll])

  return (
    <div className={classes.editorWrapper}>
      <AceEditor
        mode='javascript'
        theme='xcode'
        value={source}
        readOnly
        highlightActiveLine={false}
        showGutter
        showPrintMargin={false}
        width='100%'
        height='100%'
        fontSize={12}
        tabSize={2}
        wrapEnabled={false}
        editorProps={{ $blockScrolling: true }}
        setOptions={{
          useWorker: false,
          showFoldWidgets: false
        }}
        onLoad={handleLoad}
      />
    </div>
  )
}

DiffEditor.propTypes = {
  source: PropTypes.string.isRequired,
  markerRows: PropTypes.array.isRequired,
  lineClass: PropTypes.string.isRequired,
  gutterClass: PropTypes.string.isRequired,
  editorRef: PropTypes.object.isRequired,
  onScroll: PropTypes.func
}

function SourceDiffModal ({ currentSource, proposedSource, description, onApply, onDismiss }) {
  const classes = useStyles()
  const oldEditorRef = useRef(null)
  const newEditorRef = useRef(null)
  const syncing = useRef(false)

  const diff = useMemo(() => computeLineDiff(currentSource, proposedSource), [currentSource, proposedSource])

  const handleOldScroll = useCallback((scrollTop, scrollLeft) => {
    if (syncing.current) return
    syncing.current = true
    const target = newEditorRef.current
    if (target) {
      if (scrollTop != null) target.getSession().setScrollTop(scrollTop)
      if (scrollLeft != null) target.getSession().setScrollLeft(scrollLeft)
    }
    requestAnimationFrame(() => { syncing.current = false })
  }, [])

  const handleNewScroll = useCallback((scrollTop, scrollLeft) => {
    if (syncing.current) return
    syncing.current = true
    const target = oldEditorRef.current
    if (target) {
      if (scrollTop != null) target.getSession().setScrollTop(scrollTop)
      if (scrollLeft != null) target.getSession().setScrollLeft(scrollLeft)
    }
    requestAnimationFrame(() => { syncing.current = false })
  }, [])

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onDismiss() }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onDismiss])

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onDismiss()
  }

  return (
    <div className={classes.overlay} onClick={handleOverlayClick}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <div>
            <span className={classes.headerTitle}>Review Source Changes</span>
            {description && <span className={classes.description}>{description}</span>}
          </div>
          <button
            className={classes.closeBtn}
            onClick={onDismiss}
            aria-label='Close diff modal'
          >
            &times;
          </button>
        </div>

        <div className={classes.diffContainer} role='region' aria-label='Source code diff'>
          <div className={classes.diffPane}>
            <div className={`${classes.paneHeader} ${classes.paneHeaderOld}`}>Current</div>
            <DiffEditor
              source={currentSource}
              markerRows={diff.removedRows}
              lineClass='diff-line-removed'
              gutterClass='diff-gutter-removed'
              editorRef={oldEditorRef}
              onScroll={handleOldScroll}
            />
          </div>
          <div className={classes.diffPane}>
            <div className={`${classes.paneHeader} ${classes.paneHeaderNew}`}>Proposed</div>
            <DiffEditor
              source={proposedSource}
              markerRows={diff.addedRows}
              lineClass='diff-line-added'
              gutterClass='diff-gutter-added'
              editorRef={newEditorRef}
              onScroll={handleNewScroll}
            />
          </div>
        </div>

        <div className={classes.footer}>
          <div className={classes.stats}>
            <span className={classes.statAdded}>+{diff.added} added</span>
            <span className={classes.statRemoved}>&minus;{diff.removed} removed</span>
          </div>
          <div className={classes.footerActions}>
            <SydButton size='sm' variant='outline' onClick={onDismiss}>
              Reject
            </SydButton>
            <SydButton size='sm' variant='ai' onClick={() => onApply(proposedSource)}>
              Accept Changes
            </SydButton>
          </div>
        </div>
      </div>
    </div>
  )
}

SourceDiffModal.propTypes = {
  currentSource: PropTypes.string.isRequired,
  proposedSource: PropTypes.string.isRequired,
  description: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired
}

export default SourceDiffModal
